import { Box, ConfirmationDialog, Spinner, Text } from "@primer/react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { registrationApi } from "../../api/registration/registrationApi";
import { PageLayout } from "../../components";
import { NotifyHelper } from "../../helpers/toast";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import { useAuth } from "../../hooks/useAuth";
import { useIsMobile } from "../../hooks/useIsMobile";
import { IClassListItem } from "../../model/respone/classes/IClassListItem";
import { INhomLop } from "../../model/respone/classes/INhomLop";
import { IThoiKhoaBieu } from "../../model/respone/classes/IThoiKhoaBieu";
import { IMyRegistration } from "../../model/respone/registration/IMyRegistration";
import { rootActions } from "../../state/actions/rootActions";
import { eReducerStatusBase } from "../../state/reducer-model/eReducerStatusBase";
import { IFinancialSummary } from "../../model/respone/financial/IFinancialSummary";
import {
    formatCountdown,
    formatDateVi,
    formatThu,
    formatTiet,
    getFinancialBlock,
    registerBlockedTooltip,
} from "./classesHelpers";
import ClassesPageMobile from "./mobile/ClassesPageMobile";
import FinancialSummaryWidget from "./FinancialSummaryWidget";

/**
 * Bọc nút disabled: nhiều trình duyệt không hiện `title` trên button disabled khi hover.
 * <c>gateReason</c>: ngoài giờ đăng ký hoặc chưa tải xong thông tin thời gian từ cổng.
 */
function withLockedRegisterTooltip(
    isLocked: boolean,
    item: IClassListItem,
    isFull: boolean,
    inner: ReactNode,
    gateReason?: string
): ReactNode {
    if (!isLocked) return inner;
    return (
        <span title={registerBlockedTooltip(item, isFull, gateReason)} style={{ display: "inline-block", cursor: "not-allowed" }}>
            {inner}
        </span>
    );
}

/**
 * Render sub-table 9 cột (LTH hoặc SH) inline dưới row parent.
 * 1 dòng <tr colSpan=parentColSpan> chứa nested table.
 * onPickPair(parentClassId, childClassId) — Phase 2C atomic register; Phase 2A vẫn gọi handleRegister single.
 */
const renderSubTable = (
    classes: import("../../model/respone/classes/IClassListItem").IClassListItem[],
    type: "lth" | "sh",
    parentClassId: number,
    parentColSpan: number,
    myList: IMyRegistration[],
    pendingClassIds: number[],
    onPickPair: (parentClassId: number, childClassId: number) => void,
    hasTuition: boolean,
    financial: IFinancialSummary | null,
    gateReason?: string
): ReactNode => {
    return (
        <tr key={`sub-${type}-${parentClassId}`}>
            <td colSpan={parentColSpan} style={{ padding: 0, background: "#fafbfc" }}>
                <div style={{ padding: "8px 24px" }}>
                    <p style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                        {type === "lth" ? "Lớp thực hành đính kèm" : "Lớp song hành — đăng ký kèm"}
                    </p>
                    <table className="old-table" style={{ marginBottom: 0 }}>
                        <thead>
                            <tr>
                                <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                <th rowSpan={2}>Tên lớp</th>
                                <th colSpan={4}>Thời khóa biểu</th>
                                <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                <th rowSpan={2} style={{ width: 80 }}>Còn trống</th>
                                {hasTuition && (
                                    <>
                                        <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                    </>
                                )}
                                <th rowSpan={2} style={{ width: 80 }}>#</th>
                            </tr>
                            <tr>
                                <th>Thời gian</th>
                                <th>Thứ</th>
                                <th>Tiết</th>
                                <th>Phòng</th>
                            </tr>
                        </thead>
                        <tbody>
                            {classes.flatMap((sub, idx) => {
                                const isFull = sub.slotsLeft <= 0;
                                const isLocked = isFull || !sub.canRegister || !!gateReason;
                                const isPending = pendingClassIds.includes(sub.classId);
                                const isAlreadyRegistered = myList.some(
                                    r => r.id_lop_tc === sub.classId && r.trang_thai === 1
                                );
                                const cellsBefore = [
                                    idx + 1,
                                    <Box>
                                        <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                            {type === "sh" && sub.ten_mon ? `${sub.ten_mon} — ` : ""}
                                            {sub.ten_lop_hp}
                                        </p>
                                        {sub.hinh_thuc_dang_ky_text && (
                                            <span className="old-badge old-badge-primary">{sub.hinh_thuc_dang_ky_text}</span>
                                        )}
                                    </Box>,
                                ];
                                const cellsAfter = [
                                    isFull ? (
                                        <span className="text-danger">Hết</span>
                                    ) : (
                                        <strong>{sub.slotsLeft}</strong>
                                    ),
                                    ...(hasTuition ? [
                                        <span style={{ display: "block", textAlign: "right" }}>
                                            {(sub.so_tien ?? 0).toLocaleString("vi-VN")}
                                        </span>,
                                        <span style={{ display: "block", textAlign: "right" }}>
                                            {(sub.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                        </span>,
                                        <span style={{ display: "block", textAlign: "right" }}>
                                            {(sub.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                        </span>,
                                    ] : []),
                                    (() => {
                                        if (isAlreadyRegistered) return (
                                            <span className="text-success">
                                                <i className="fas fa-check" /> Đã ĐK
                                            </span>
                                        );
                                        if (isPending) return (
                                            <button className="old-btn old-btn-outline-primary" disabled>
                                                <Spinner size="small" />
                                            </button>
                                        );
                                        // FE-side financial gate (strict mode) — ẩn nút Đăng ký khi:
                                        //   1. SV chưa có hồ sơ tài chính.
                                        //   2. Tổng phải nộp sau khi thêm lớp này > số dư.
                                        // Backend EligibilityService vẫn enforce (defense-in-depth).
                                        const finBlock = getFinancialBlock(sub, financial);
                                        if (finBlock) return (
                                            <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                {finBlock}
                                            </span>
                                        );
                                        return withLockedRegisterTooltip(
                                            isLocked,
                                            sub,
                                            isFull,
                                            <button
                                                className="old-btn old-btn-outline-primary"
                                                disabled={isLocked}
                                                onClick={() => onPickPair(parentClassId, sub.classId)}
                                            >
                                                Đăng ký
                                            </button>,
                                            gateReason
                                        );
                                    })(),
                                ];
                                const hasConflict = !!(sub.iD_lops_TrungLich && sub.iD_lops_TrungLich.length > 0);
                                const highlightClass = isAlreadyRegistered
                                    ? undefined
                                    : (isFull || hasConflict ? "old-row-locked" : undefined);
                                return renderTKBRows(
                                    sub.thoiKhoaBieus,
                                    cellsBefore,
                                    cellsAfter,
                                    `sub-${parentClassId}-${sub.classId}-${idx}`,
                                    highlightClass,
                                    sub.giao_vien
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </td>
        </tr>
    );
};

/**
 * Clone Old `Home/Index.cshtml` — 3 sections cùng 1 page:
 *   [#Registered]  Top — danh sách lớp đã đăng ký + nút "Lưu dữ liệu đăng ký"
 *   [#listSubject] Bottom-left 55% — danh sách học phần đăng ký học (1 row/môn)
 *   [#listDetail]  Bottom-right 50% — danh sách lớp tín chỉ của môn đang chọn
 *
 * Match video hướng dẫn cũ — vị trí nút "Chọn", "Hủy", "Lưu" giữ nguyên.
 *
 * Multi-row TKB rendering: 1 đăng ký có nhiều buổi/tuần → STT/Mã HP/Tên HP/... rowSpan,
 * Thời gian/Thứ/Tiết/Phòng render từng row riêng.
 */

/**
 * Render N row TKB. cellsBefore/cellsAfter rowSpan=N (chỉ render ở row đầu).
 * Cột TKB gồm Thời gian / Thứ / Tiết / Phòng + Giáo viên (per-row vì 1 lớp có thể nhiều GV).
 * classLevelGv là fallback khi tkb.ho_ten thiếu.
 */
const renderTKBRows = (
    tkbs: IThoiKhoaBieu[] | undefined,
    cellsBefore: React.ReactNode[],
    cellsAfter: React.ReactNode[],
    rowKey: string | number,
    rowClassName?: string,
    classLevelGv?: string
): React.ReactNode => {
    const list = tkbs && tkbs.length > 0 ? tkbs : [{} as IThoiKhoaBieu];
    return list.map((tkb, idx) => (
        <tr key={`${rowKey}-${idx}`} className={rowClassName}>
            {idx === 0 &&
                cellsBefore.map((cell, i) => (
                    <td key={`b${i}`} rowSpan={list.length} className="text-center">
                        {cell}
                    </td>
                ))}
            <td className="text-center">
                {tkb.tu_ngay && tkb.den_ngay ? `${tkb.tu_ngay} - ${tkb.den_ngay}` : "—"}
            </td>
            <td className="text-center">{tkb.thu !== undefined ? formatThu(tkb.thu) : "—"}</td>
            <td className="text-center">{formatTiet(tkb.tiet, tkb.so_tiet)}</td>
            <td className="text-center">{tkb.phong || "—"}</td>
            <td className="text-center">{tkb.ho_ten || classLevelGv || "—"}</td>
            {idx === 0 &&
                cellsAfter.map((cell, i) => (
                    <td key={`a${i}`} rowSpan={list.length} className="text-center">
                        {cell}
                    </td>
                ))}
        </tr>
    ));
};

const ClassesPage = () => {
    const dispatch = useAppDispatch();
    const isMobile = useIsMobile();
    const { user } = useAuth();
    const { status, list, error_message: classesErrorMessage } = useAppSelector(s => s.classes);
    const { myStatus, myList, cancellingClassIds, pendingClassIds, confirmedClassIds, chotDuLieuStatus } =
        useAppSelector(s => s.registration);
    const { openAt, closeAt, enforceTimeWindow, status: queueStatus, error_message: queueErrorMessage } =
        useAppSelector(s => s.queue);
    const financial = useAppSelector(s => s.financial.data);

    /** Tick mỗi giây ép re-render → countdown + ranh giới open/close chuyển trạng thái tự động.
     *  Chỉ chạy khi đang trước openAt → tránh setInterval vĩnh viễn suốt phiên đăng ký bình thường. */
    const [, setTick] = useState(0);
    const openTs = openAt ? new Date(openAt).getTime() : null;
    const closeTs = closeAt ? new Date(closeAt).getTime() : null;
    const beforeOpenNow = !!(enforceTimeWindow && openTs != null && !Number.isNaN(openTs) && Date.now() < openTs);
    useEffect(() => {
        if (!beforeOpenNow) return;
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, [beforeOpenNow]);

    /** Trạng thái cửa sổ thời gian đăng ký — compute mỗi render (rẻ) để boolean cập nhật
     *  ngay khi vượt qua openAt/closeAt mà không cần memo phức tạp.
     *  enforceTimeWindow=false → skip toàn bộ gate (legacy behavior). */
    const now = Date.now();
    const beforeOpen = enforceTimeWindow && openTs != null && !Number.isNaN(openTs) && now < openTs;
    const afterClose = enforceTimeWindow && closeTs != null && !Number.isNaN(closeTs) && now > closeTs;
    const timeWindowReason: string | undefined = beforeOpen
        ? `Chưa đến thời gian đăng ký. Cổng mở lúc ${formatDateVi(openAt)}.`
        : afterClose
            ? `Đã quá thời gian đăng ký. Cổng đóng lúc ${formatDateVi(closeAt)}.`
            : undefined;

    /** Khóa UI đăng ký khi chưa có snapshot thời gian từ GET /queue/status — tránh nút enable sớm hơn backend. */
    const registerGateReason: string | undefined = (() => {
        if (!enforceTimeWindow) return undefined;
        if (timeWindowReason) return timeWindowReason;
        if (queueStatus === eReducerStatusBase.is_error) {
            return queueErrorMessage?.trim() || "Không tải được trạng thái hàng chờ. Vui lòng F5 hoặc thử lại sau.";
        }
        if (queueStatus !== eReducerStatusBase.is_completed) {
            return "Đang tải thông tin thời gian đăng ký từ cổng. Vui lòng đợi...";
        }
        const missingWindow =
            !openAt?.trim() || !closeAt?.trim() ||
            openTs == null || Number.isNaN(openTs) ||
            closeTs == null || Number.isNaN(closeTs);
        if (missingWindow) return "Chưa nhận được khung thời gian đăng ký từ cổng. Vui lòng tải lại trang (F5).";
        return undefined;
    })();

    /** Filter: hình thức đăng ký cho cả 2 bảng. 0=tất cả. */
    const [hinhThucReg, setHinhThucReg] = useState<number>(0);  // Registered table filter
    const [hinhThuc, setHinhThuc] = useState<number>(0);        // listSubject filter
    /** Filter: nhóm lớp học phần (Old `select_nhom_lop`). "" = tất cả. Value = ma_nhom. */
    const [nhomLop, setNhomLop] = useState<string>("");
    /** Catalog nhóm lớp đợt hiện tại — load từ Redis `reg:catalog:nhom-lop` qua API. */
    const [nhomLops, setNhomLops] = useState<INhomLop[]>([]);
    /** Môn đang chọn — drives listDetail. */
    /** Dòng học phần đang chọn — định danh theo composite `id_mon|so_tin_chi`, KHÔNG phải id_mon. */
    const [selectedSubjectKey, setSelectedSubjectKey] = useState<string | null>(null);
    /** Confirm dialog state cho hủy đăng ký. */
    const [confirmCancel, setConfirmCancel] = useState<IMyRegistration | null>(null);
    /** Confirm dialog state cho chốt dữ liệu. */
    const [confirmChot, setConfirmChot] = useState(false);
    /** Phase 2: parent class id đang expand sub-table (LTH hoặc SH). null = không expand. */
    const [expandedSub, setExpandedSub] = useState<{ type: "lth" | "sh"; classId: number } | null>(null);

    /** Luôn gọi /queue/status khi vào trang (deep link /classes, reload) — không phụ thuộc LandingPage.
     *  Saga takeLatest gộp request trùng nhau. */
    useEffect(() => {
        if (!user) return;
        dispatch(rootActions.queue.LOAD_STATUS_START());
    }, [user, dispatch]);

    useEffect(() => {
        if (status === eReducerStatusBase.is_not_initialization) {
            dispatch(rootActions.classes.LOAD_START());
        }
        if (myStatus === eReducerStatusBase.is_not_initialization) {
            dispatch(rootActions.registration.LOAD_MY_START());
        }
    }, [status, myStatus, dispatch]);

    /** Bắt đầu vòng lặp poll số dư tài chính (mỗi 60s) khi vào trang đăng ký. */
    useEffect(() => {
        dispatch(rootActions.financial.LOAD_START());
    }, [dispatch]);

    /** Fetch nhóm lớp catalog 1 lần lúc mount. Catalog stable trong 1 đợt → không cần refresh.
     *  Local state thay vì Redux vì payload nhỏ + chỉ dùng ở page này. */
    useEffect(() => {
        let cancelled = false;
        registrationApi.GetNhomLops().then(res => {
            if (!cancelled && res.is_success && res.data) {
                setNhomLops(res.data);
            }
        });
        return () => { cancelled = true; };
    }, []);

    

    /** Group lớp theo (id_mon + so_tin_chi) cho listSubject — cùng 1 id_mon nhưng khác số tín
     *  (môn nằm ở các CTDT khác nhau) phải tách thành 2 dòng học phần riêng. */
    const subjects = useMemo(() => {
        const map = new Map<
            string,
            {
                subjectKey: string;
                id_mon: number;
                ten_mon: string;
                ky_hieu: string;
                ky_thu: number | undefined;
                nhom_tu_chon_text: string | undefined;
                nhom_TuChon_Chung: string | undefined;
                rang_buoc_text: string | undefined;
                hinh_thuc_dang_ky: number | undefined;
                hinh_thuc_dang_ky_text: string | undefined;
                tinh_chat_lop_text: string | undefined;
                so_tin_chi: number;
                classes: IClassListItem[];
            }
        >();
        list.forEach(c => {
            const subjectKey = `${c.id_mon}|${c.so_tin_chi}`;
            if (!map.has(subjectKey)) {
                map.set(subjectKey, {
                    subjectKey,
                    id_mon: c.id_mon,
                    ten_mon: c.ten_mon,
                    ky_hieu: c.ky_hieu ?? "",
                    ky_thu: c.ky_thu,
                    nhom_tu_chon_text: c.nhom_tu_chon_text,
                    nhom_TuChon_Chung: c.nhom_TuChon_Chung,
                    rang_buoc_text: c.rang_buoc_text,
                    hinh_thuc_dang_ky: c.hinh_thuc_dang_ky,
                    hinh_thuc_dang_ky_text: c.hinh_thuc_dang_ky_text,
                    tinh_chat_lop_text: c.tinh_chat_lop_text,
                    so_tin_chi: c.so_tin_chi,
                    classes: [],
                });
            }
            map.get(subjectKey)!.classes.push(c);
        });
        return Array.from(map.values());
    }, [list]);

    const filteredSubjects = useMemo(() => {
        return subjects.filter(s => {
            if (hinhThuc !== 0 && s.hinh_thuc_dang_ky !== hinhThuc) return false;
            if (nhomLop) {
                const hasNhomLop = s.classes.some(c => c.nhom_lop === nhomLop);
                if (!hasNhomLop) return false;
            }
            return true;
        });
    }, [subjects, hinhThuc, nhomLop]);

    /**
     * Bảng 4 listDetailWithSubject — flat list class match (nhomLop + hinhThuc) filter.
     * Mỗi class = 1 row trong B4 (môn info duplicated per class). Hiển thị khi user chọn nhomLop.
     */
    const filteredCombinedClasses = useMemo(() => {
        return list.filter(c => {
            if (nhomLop && c.nhom_lop !== nhomLop) return false;
            if (hinhThuc !== 0 && c.hinh_thuc_dang_ky !== hinhThuc) return false;
            return true;
        });
    }, [list, nhomLop, hinhThuc]);

    /** Options cho dropdown "Chọn nhóm lớp" — lấy từ catalog Redis (ESS Module A nạp).
     *  Sort theo ten_nhom A→Z cho UX. Value = ma_nhom (match c.nhom_lop trên class). */
    const nhomLopOptions = useMemo(() => {
        return [...nhomLops].sort((a, b) =>
            a.ten_nhom.localeCompare(b.ten_nhom, "vi", { sensitivity: "base" }));
    }, [nhomLops]);

    const selectedSubject = useMemo(
        () => subjects.find(s => s.subjectKey === selectedSubjectKey) ?? null,
        [subjects, selectedSubjectKey]
    );

    /** Đăng ký đã confirmed (Registered table) — filter theo hình thức đăng ký. */
    const registeredList = useMemo(() => {
        const confirmed = myList.filter(r => r.trang_thai === 1);
        if (hinhThucReg === 0) return confirmed;
        return confirmed.filter(r => r.hinh_thuc_dang_ky === hinhThucReg);
    }, [myList, hinhThucReg]);

    const tongTC = useMemo(
        () => registeredList.reduce((sum, r) => sum + (r.so_tin_chi || 0), 0),
        [registeredList]
    );

    /** Đợt có bật học phí theo số dư → 3 cột tiền (Số tiền / Miễn giảm / Phải nộp) LUÔN hiện.
     *  Lớp thiếu đơn giá hoặc TC=0 vẫn hiển thị 0đ — không ẩn cột để SV biết đợt đang bật cờ tài chính.
     *  Cờ off → ẩn hoàn toàn (đợt thường không liên quan tiền). Trước đây dùng proxy
     *  `list.some(so_tien > 0)` — proxy này sai khi mọi lớp thiếu đơn giá: cờ on nhưng 3 cột bị ẩn,
     *  che giấu lỗi setup data thay vì phơi bày. */
    const hasTuition = !!financial?.tinh_hoc_phi_theo_so_du;

    const tcMin = user?.so_tc_min_duoc_dk ?? 0;
    const tcMax = user?.so_tc_max_duoc_dk ?? 0;

    // isChotDuLieu derive từ myList (Backend Registrations.is_chot_du_lieu) thay vì user-level flag.
    // Lý do: chốt là per-row trong DB, sync về FE qua /api/registration/my. user object cũ giữ
    // optional field nhưng không còn nguồn truth.
    const chotInfo = useMemo(() => {
        const chotRows = myList.filter(r => r.isChotDuLieu && r.ngayChotDuLieu);
        if (chotRows.length === 0) return { isChotDuLieu: false, ngayChot: null as Date | null };
        const maxIso = chotRows.reduce<string>((m, r) => {
            const v = r.ngayChotDuLieu!;
            return !m || v > m ? v : m;
        }, "");
        return { isChotDuLieu: true, ngayChot: new Date(maxIso) };
    }, [myList]);
    const isChotDuLieu = chotInfo.isChotDuLieu;
    const isChottingNow = chotDuLieuStatus === eReducerStatusBase.is_loading;

    /** Loading flags cho table-level spinner. is_completed/is_error → đã xong; còn lại = loading. */
    const classesLoading =
        status !== eReducerStatusBase.is_completed &&
        status !== eReducerStatusBase.is_error;
    const myLoading =
        myStatus !== eReducerStatusBase.is_completed &&
        myStatus !== eReducerStatusBase.is_error;

    /** 1 row spinner placeholder dùng cho mọi bảng khi loading + chưa có data. */
    const renderLoadingRow = (colSpan: number, label = "Đang tải...") => (
        <tr>
            <td colSpan={colSpan} style={{ textAlign: "center", padding: 20 }}>
                <Spinner size="small" />
                <span style={{ marginLeft: 8, color: "#888" }}>{label}</span>
            </td>
        </tr>
    );

    /** Refresh slot count cho danh sách classIds qua endpoint nhẹ /api/registration/slots. */
    const refreshSlots = async (classIds: number[]) => {
        if (classIds.length === 0) return;
        const res = await registrationApi.GetSlots(classIds);
        if (res.is_success && res.data) {
            dispatch(rootActions.classes.UPDATE_SLOTS(res.data));
        }
    };

    const handleSelectSubject = (subjectKey: string) => {
        setSelectedSubjectKey(prev => {
            const next = prev === subjectKey ? null : subjectKey;
            // Auto refresh slot khi user CHỌN môn (next != null) → cột "Còn trống" Bảng 3 fresh.
            if (next !== null) {
                const subj = subjects.find(s => s.subjectKey === next);
                if (subj) refreshSlots(subj.classes.map(c => c.classId));
            }
            return next;
        });
    };

    /** Manual button "Tải lại" — refresh slot của các lớp đang hiển thị (Bảng 3 hoặc Bảng 4). */
    const handleRefreshSlots = () => {
        const visibleIds = selectedSubject
            ? selectedSubject.classes.map(c => c.classId)
            : filteredCombinedClasses.map(c => c.classId);
        refreshSlots(visibleIds);
    };

    const handleRegister = (classId: number) => {
        dispatch(rootActions.registration.REGISTER_START({ classId }));
    };

    const handleCancelConfirm = () => {
        if (confirmCancel) {
            dispatch(rootActions.registration.CANCEL_START({ classId: confirmCancel.id_lop_tc }));
        }
        setConfirmCancel(null);
    };

    // Click button "Lưu dữ liệu đăng ký": guard min/max trước khi mở dialog confirm.
    // Pattern này tốt hơn chạy guard SAU khi user đã confirm (không phải mở dialog rồi báo lỗi).
    // Mirror Old: check min chặn cứng; max đã chặn ở flow đăng ký nhưng vẫn check defensive.
    const handleChotDuLieuClick = () => {
        if (registeredList.length === 0) {
            NotifyHelper.Error("Bạn chưa đăng ký lớp nào để lưu dữ liệu.");
            return;
        }
        if (tcMin > 0 && tongTC < tcMin) {
            NotifyHelper.Error(`Bạn chưa đăng ký đủ số tín chỉ tối thiểu (${tongTC}/${tcMin} TC).`);
            return;
        }
        if (tcMax > 0 && tongTC > tcMax) {
            NotifyHelper.Error(`Tổng tín chỉ vượt giới hạn tối đa (${tongTC}/${tcMax} TC).`);
            return;
        }
        setConfirmChot(true);
    };

    // User confirm trong dialog → dispatch saga; saga toast success, reload myList.
    const handleChotDuLieuConfirm = () => {
        dispatch(rootActions.registration.CHOT_DU_LIEU_START());
        setConfirmChot(false);
    };

    /** Đổi nhóm lớp — gom side-effect đang inline ở <select> desktop để bản mobile dùng chung.
     *  Reset môn đang chọn + refresh slot Bảng 4 khi chọn 1 nhóm cụ thể. */
    const handleChangeNhomLop = (value: string) => {
        setNhomLop(value);
        setSelectedSubjectKey(null);
        if (value !== "") {
            const ids = list.filter(c => c.nhom_lop === value).map(c => c.classId);
            refreshSlots(ids);
        }
    };

    /** Toggle expand sub-table LTH/SH — cùng logic với onClick desktop. */
    const handleToggleExpand = (type: "lth" | "sh", classId: number) => {
        setExpandedSub(prev => (prev?.classId === classId ? null : { type, classId }));
    };

    /** Đăng ký cặp lớp cha + lớp con (LTH/SH) — cùng action desktop dùng. */
    const handleRegisterPair = (parentClassId: number, childClassId: number) => {
        dispatch(rootActions.registration.REGISTER_PAIR_START({ parentClassId, childClassId }));
    };

    /** Nhánh render mobile (<768px) — tái dùng toàn bộ state/memo/handler ở trên,
     *  chỉ thay tầng giao diện. Desktop giữ nguyên ở phần `return` bên dưới. */
    if (isMobile) {
        return (
            <PageLayout title="Đăng ký tín chỉ" maxWidth="100%" sx={{ p: 0, px: 0, py: 0 }}>
                <Box sx={{ px: "12px", pt: "12px" }}>
                    <FinancialSummaryWidget />
                </Box>
                <ClassesPageMobile
                    enforceTimeWindow={enforceTimeWindow}
                    beforeOpen={beforeOpen}
                    afterClose={afterClose}
                    openAt={openAt}
                    closeAt={closeAt}
                    openTs={openTs}
                    now={now}
                    registerGateReason={registerGateReason}
                    queueStatus={queueStatus}
                    regTime={{
                        // Ưu tiên giờ ĐỢT (openAt/closeAt từ Redis) giống Header — ESS sửa đợt
                        // thì F5 thấy ngay; fallback claim JWT khi đợt chưa có dữ liệu.
                        start: openAt ?? user?.tu_ngay_dkhocdi ?? user?.thoi_gian_bat_dau ?? user?.thoi_gian_bat_dau_toan_truong,
                        end: closeAt ?? user?.den_ngay_dkhocdi ?? user?.thoi_gian_ket_thuc ?? user?.thoi_gian_ket_thuc_toan_truong,
                    }}
                    filteredSubjects={filteredSubjects}
                    filteredCombinedClasses={filteredCombinedClasses}
                    nhomLopOptions={nhomLopOptions}
                    registeredList={registeredList}
                    myList={myList}
                    hinhThuc={hinhThuc}
                    hinhThucReg={hinhThucReg}
                    nhomLop={nhomLop}
                    onApplyFilter={(ht, nl) => { setHinhThuc(ht); handleChangeNhomLop(nl); }}
                    onChangeHinhThucReg={setHinhThucReg}
                    selectedSubjectKey={selectedSubjectKey}
                    onSelectSubject={handleSelectSubject}
                    expandedSub={expandedSub}
                    onToggleExpand={handleToggleExpand}
                    classesLoading={classesLoading}
                    classesError={status === eReducerStatusBase.is_error}
                    classesErrorMessage={classesErrorMessage}
                    myLoading={myLoading}
                    isChotDuLieu={isChotDuLieu}
                    isChottingNow={isChottingNow}
                    ngayChot={chotInfo.ngayChot}
                    tongTC={tongTC}
                    tcMin={tcMin}
                    tcMax={tcMax}
                    pendingClassIds={pendingClassIds}
                    confirmedClassIds={confirmedClassIds}
                    cancellingClassIds={cancellingClassIds}
                    onRegister={handleRegister}
                    onPickPair={handleRegisterPair}
                    onRefreshSlots={handleRefreshSlots}
                    onRetryLoad={() => dispatch(rootActions.classes.LOAD_START())}
                    onChotClick={handleChotDuLieuClick}
                    confirmCancel={confirmCancel}
                    setConfirmCancel={setConfirmCancel}
                    onCancelConfirm={handleCancelConfirm}
                    confirmChot={confirmChot}
                    setConfirmChot={setConfirmChot}
                    onChotConfirm={handleChotDuLieuConfirm}
                />
            </PageLayout>
        );
    }

    return (
        <PageLayout title="Đăng ký tín chỉ" maxWidth="100%" sx={{ p: "15px" }}>
            {/* Banner gate theo time window — chỉ hiển thị khi Registration:EnforceTimeWindow=true
                và đang ngoài [openAt, closeAt]. Trùng UX với server-side 403 OUT_OF_TIME_WINDOW. */}
            {beforeOpen && openAt && (
                <Box
                    sx={{
                        bg: "#fff3cd",
                        color: "#856404",
                        border: "1px solid #ffeeba",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                    }}
                >
                    Chưa đến thời gian đăng ký. Cổng mở lúc{" "}
                    <strong>{formatDateVi(openAt)}</strong>.
                    {openTs != null && (
                        <> Còn lại <strong>{formatCountdown(openTs - now)}</strong>.</>
                    )}
                    <br />
                    <span style={{ fontSize: 12, fontStyle: "italic" }}>
                        Bạn có thể xem trước danh sách lớp ở bên dưới. Nút "Đăng ký" sẽ được bật khi đến giờ.
                    </span>
                </Box>
            )}
            {afterClose && closeAt && (
                <Box
                    sx={{
                        bg: "#f8d7da",
                        color: "#721c24",
                        border: "1px solid #f5c6cb",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                    }}
                >
                    Đã quá thời gian đăng ký. Cổng đóng lúc{" "}
                    <strong>{formatDateVi(closeAt)}</strong>.
                </Box>
            )}

            {enforceTimeWindow && registerGateReason && !beforeOpen && !afterClose && (
                <Box
                    sx={{
                        bg: "#e7f3ff",
                        color: "#004085",
                        border: "1px solid #b8daff",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                    }}
                >
                    {(queueStatus === eReducerStatusBase.is_loading ||
                        queueStatus === eReducerStatusBase.is_not_initialization) && (
                        <Spinner size="small" />
                    )}
                    <span>{registerGateReason}</span>
                </Box>
            )}

            {/* Banner lỗi tải danh sách lớp — vd 429 rate-limit ("Bạn đang gửi quá nhiều yêu cầu...").
                Trước đây 429 chỉ làm bảng trống, SV tưởng "không có lớp" → hiện rõ lý do + nút thử lại. */}
            {status === eReducerStatusBase.is_error && (
                <Box
                    sx={{
                        bg: "#f8d7da",
                        color: "#721c24",
                        border: "1px solid #f5c6cb",
                        borderRadius: 2,
                        textAlign: "center",
                        py: 2,
                        px: 3,
                        mb: 2,
                        fontWeight: 500,
                        fontSize: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <span>
                        {classesErrorMessage?.trim() ||
                            "Không tải được danh sách lớp. Vui lòng thử lại sau giây lát."}
                    </span>
                    <button
                        type="button"
                        className="old-btn old-btn-outline-danger"
                        onClick={() => dispatch(rootActions.classes.LOAD_START())}
                    >
                        Tải lại
                    </button>
                </Box>
            )}

            {/* Time info banner top right */}
            {(user?.thoi_gian_bat_dau || user?.thoi_gian_bat_dau_toan_truong) && (
                <Box
                    sx={{
                        textAlign: "right",
                        color: "red",
                        fontSize: 1,
                        mb: 2,
                        lineHeight: 1.6,
                    }}
                >
                    {user?.thoi_gian_bat_dau && user?.thoi_gian_ket_thuc && (
                        <div>
                            Thời gian đăng ký học bắt đầu từ{" "}
                            <strong>{user.thoi_gian_bat_dau}</strong> đến{" "}
                            <strong>{user.thoi_gian_ket_thuc}</strong>
                        </div>
                    )}
                    {user?.thoi_gian_bat_dau_toan_truong && user?.thoi_gian_ket_thuc_toan_truong && (
                        <div>
                            Thời gian đăng ký học toàn trường bắt đầu từ{" "}
                            <strong>{user.thoi_gian_bat_dau_toan_truong}</strong> đến{" "}
                            <strong>{user.thoi_gian_ket_thuc_toan_truong}</strong>
                        </div>
                    )}
                </Box>
            )}

            {/* Banner tài chính — số dư + miễn giảm SV (poll 60s) */}
            <FinancialSummaryWidget />

            {/* ===================== SECTION 1: REGISTERED ===================== */}
            <Box id="Registered" sx={{ mb: 4 }}>
                <div className="old-section-title">
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 2,
                            flexWrap: "wrap",
                            flex: 1,
                        }}
                    >
                        <span>Danh sách các lớp tín chỉ đã đăng ký</span>
                        <select
                            value={hinhThucReg}
                            onChange={(e) => setHinhThucReg(Number(e.target.value))}
                            className="old-form-control"
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value={0}>Tất cả hình thức</option>
                            <option value={1}>Học chính khóa</option>
                            <option value={2}>Học lại, học cải thiện điểm, học bù (lớp riêng)</option>
                            <option value={3}>Học lại, học cải thiện điểm, học bù (lớp ghép)</option>
                        </select>
                        <Text
                            sx={{
                                fontSize: 14,
                                color: "red",
                                fontWeight: 500,
                                ml: 1,
                            }}
                        >
                            Số tín chỉ được đăng ký: tối thiểu: <b>{tcMin}</b>, tối đa: <b>{tcMax}</b>, đã đăng ký: <b>{tongTC}</b>
                        </Text>
                    </Box>
                    <Box sx={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        {!isChotDuLieu && (
                            <button
                                className="old-btn old-btn-success old-btn-pulse"
                                onClick={handleChotDuLieuClick}
                                disabled={registeredList.length === 0 || isChottingNow || !!registerGateReason}
                                title={
                                    registerGateReason
                                        ? registerGateReason
                                        : "Lưu dữ liệu đăng ký — sau khi lưu sẽ không thể thay đổi"
                                }
                            >
                                {isChottingNow ? <Spinner size="small" /> : "Lưu dữ liệu đăng ký"}
                            </button>
                        )}
                    </Box>
                </div>

                {/* Cảnh báo chưa lưu — kết quả chưa được công nhận */}
                {!isChotDuLieu && registeredList.length > 0 && (
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            bg: "#fff8e6",
                            border: "1px solid #ffe2a8",
                            borderLeft: "4px solid #f0a500",
                            borderRadius: 2,
                            p: "12px 14px",
                            mb: 2,
                        }}
                    >
                        <i
                            className="fas fa-triangle-exclamation"
                            style={{ color: "#e8950c", fontSize: 20, marginTop: 2, flexShrink: 0 }}
                        />
                        <Box>
                            <Text sx={{ display: "block", fontWeight: 700, fontSize: 1, color: "#8a5a00", mb: "2px" }}>
                                Chưa lưu — kết quả chưa được công nhận
                            </Text>
                            <Text sx={{ display: "block", fontSize: 1, color: "#6b5012", lineHeight: 1.5 }}>
                                Các lớp bạn đã đăng ký <strong>chưa được lưu</strong>. Hãy bấm{" "}
                                <strong>"Lưu dữ liệu đăng ký"</strong> ở trên. Nếu không lưu, kết quả
                                đăng ký sẽ <strong>không được công nhận và xét duyệt</strong>.
                            </Text>
                        </Box>
                    </Box>
                )}

                {/* Status banner khi đã chốt */}
                {isChotDuLieu && (
                    <Box
                        sx={{
                            bg: "#dc3545",
                            color: "#fff",
                            textAlign: "center",
                            py: 2,
                            mb: 2,
                            fontWeight: 500,
                            fontSize: 1,
                        }}
                    >
                        Bạn đã lưu dữ liệu đăng ký
                        {chotInfo.ngayChot && (
                            <> vào lúc <strong>{chotInfo.ngayChot.toLocaleString("vi-VN", {
                                hour: "2-digit",
                                minute: "2-digit",
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                            })}</strong></>
                        )}.
                        Dữ liệu sau khi lưu sẽ không được thay đổi
                    </Box>
                )}

                <div style={{ overflowX: "auto" }}>
                    <table className="old-table">
                        <thead>
                            <tr>
                                <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                <th rowSpan={2} style={{ width: 110 }}>Mã học phần</th>
                                <th rowSpan={2}>Tên học phần</th>
                                <th rowSpan={2} style={{ width: 80 }}>Số tín chỉ</th>
                                <th rowSpan={2} style={{ width: 130 }}>Tên lớp</th>
                                <th rowSpan={2} style={{ width: 100 }}>Loại lớp</th>
                                <th colSpan={4}>Thời khóa biểu</th>
                                <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                {hasTuition && (
                                    <>
                                        <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                    </>
                                )}
                                <th rowSpan={2} style={{ width: 100 }}>Trạng thái</th>
                                <th rowSpan={2} style={{ width: 80 }}>#</th>
                            </tr>
                            <tr>
                                <th>Thời gian</th>
                                <th>Thứ</th>
                                <th>Tiết</th>
                                <th>Phòng</th>
                            </tr>
                        </thead>
                        <tbody>
                            {myLoading && registeredList.length === 0 ? (
                                renderLoadingRow(hasTuition ? 16 : 13)
                            ) : registeredList.length === 0 ? (
                                <tr>
                                    <td colSpan={hasTuition ? 16 : 13} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                        Bạn chưa đăng ký lớp nào trong đợt này.
                                    </td>
                                </tr>
                            ) : (
                                registeredList.flatMap((row, idx) => {
                                    const isCancelling = cancellingClassIds.includes(row.id_lop_tc);
                                    const cellsBefore = [
                                        idx + 1,
                                        row.ma_hoc_phan ?? "—",
                                        <Box>
                                            <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                {row.ten_hoc_phan ?? "—"}
                                            </p>
                                            {row.hinh_thuc_dang_ky_text && (
                                                <span className="old-badge old-badge-primary">
                                                    {row.hinh_thuc_dang_ky_text}
                                                </span>
                                            )}
                                            {row.tinh_chat_lop_text && (
                                                <span className="old-badge old-badge-success">
                                                    {row.tinh_chat_lop_text}
                                                </span>
                                            )}
                                        </Box>,
                                        row.so_tin_chi ?? "—",
                                        row.ten_lop_hp ?? `#${row.id_lop_tc}`,
                                        row.loai_lop ?? "—",
                                    ];
                                    const cellsAfter = [
                                        // 3 cột tiền — snapshot từ Registration row (đã chốt tại register-time).
                                        // KHÔNG dùng so_tien của ClassListItem hiện tại vì policy/đơn giá có thể đổi.
                                        ...(hasTuition ? [
                                            <span style={{ display: "block", textAlign: "right" }}>
                                                {(row.so_tien ?? 0).toLocaleString("vi-VN")}
                                            </span>,
                                            <span style={{ display: "block", textAlign: "right" }}>
                                                {(row.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                            </span>,
                                            <span style={{ display: "block", textAlign: "right" }}>
                                                {(row.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                            </span>,
                                        ] : []),
                                        row.duyet_dang_ky ? (
                                            <span className="text-success">Đã duyệt</span>
                                        ) : (
                                            <span className="text-danger">Chưa duyệt</span>
                                        ),
                                        !isChotDuLieu && !row.duyet_dang_ky ? (
                                            <button
                                                className="old-btn old-btn-outline-danger"
                                                disabled={isCancelling || !!registerGateReason}
                                                title={registerGateReason || "Huỷ đăng ký lớp này"}
                                                onClick={() => setConfirmCancel(row)}
                                            >
                                                {isCancelling ? <Spinner size="small" /> : "Hủy"}
                                            </button>
                                        ) : (
                                            <span style={{ color: "#888" }}>—</span>
                                        ),
                                    ];
                                    return renderTKBRows(
                                        row.thoiKhoaBieus,
                                        cellsBefore,
                                        cellsAfter,
                                        `reg-${row.id}`,
                                        undefined,
                                        row.giao_vien
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </Box>

            {/* ============ SECTION 2 + 3 + 4: REGISTERING ============
                 nhomLop === "" → split B2 (listSubject) + B3 (listDetail)
                 nhomLop !== "" → B4 (listDetailWithSubject) fullwidth, replace B2+3
              ============================================================ */}
            {!isChotDuLieu && (
                <Box id="Registering">
                    {enforceTimeWindow &&
                    queueStatus !== eReducerStatusBase.is_completed &&
                    queueStatus !== eReducerStatusBase.is_error ? (
                        // Chặn toàn bộ UI đăng ký khi /queue/status chưa trả snapshot thời gian.
                        // Tránh hiển thị nút "Chọn"/"Đăng ký" rồi mới chặn ở backend — UX không nhất quán
                        // (button enable nhưng backend reject OUT_OF_TIME_WINDOW).
                        <Box
                            sx={{
                                textAlign: "center",
                                py: 5,
                                bg: "#fff",
                                border: "1px solid #eee",
                                borderRadius: 2,
                            }}
                        >
                            <Spinner size="large" />
                            <Text as="p" sx={{ mt: 2, color: "#666", fontSize: 1 }}>
                                Đang tải thông tin thời gian đăng ký từ cổng. Vui lòng đợi...
                            </Text>
                        </Box>
                    ) : (
                    <>
                    {/* Filter bar — apply cho cả B2+3 lẫn B4 */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "baseline",
                            gap: 2,
                            flexWrap: "wrap",
                            mb: 2,
                        }}
                    >
                        <div className="old-section-title" style={{ flex: 1, minWidth: 220 }}>
                            <span>
                                {nhomLop === ""
                                    ? "Danh sách học phần đăng ký học"
                                    : "Danh sách lớp tín chỉ theo nhóm lớp"}
                            </span>
                        </div>
                        <select
                            value={hinhThuc}
                            onChange={(e) => setHinhThuc(Number(e.target.value))}
                            className="old-form-control"
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value={0}>Tất cả hình thức</option>
                            <option value={1}>Học chính khóa</option>
                            <option value={2}>Học lại, học cải thiện điểm, học bù (lớp riêng)</option>
                            <option value={3}>Học lại, học cải thiện điểm, học bù (lớp ghép)</option>
                        </select>
                        <select
                            value={nhomLop}
                            onChange={(e) => {
                                const next = e.target.value;
                                setNhomLop(next);
                                setSelectedSubjectKey(null);
                                // Auto refresh slot Bảng 4 khi user CHỌN nhóm lớp (next !== "")
                                if (next !== "") {
                                    const ids = list
                                        .filter(c => c.nhom_lop === next)
                                        .map(c => c.classId);
                                    refreshSlots(ids);
                                }
                            }}
                            className="old-form-control"
                            style={{ width: "auto", display: "inline-block" }}
                        >
                            <option value="">-- Chọn nhóm lớp --</option>
                            {nhomLopOptions.map(nl => (
                                <option key={nl.id} value={nl.ma_nhom}>
                                    {nl.ten_nhom}
                                </option>
                            ))}
                        </select>
                    </Box>

                    {nhomLop === "" ? (
                    <Box
                        sx={{
                            display: "flex",
                            gap: "15px",
                            flexDirection: ["column", "column", "row"],
                        }}
                    >
                    {/* ----- listSubject 55% ----- */}
                    <Box id="listSubject" sx={{ width: ["100%", "100%", "55%"], bg: "#fff" }}>
                        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}>
                            <table className="old-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: 50 }}>STT</th>
                                        <th style={{ width: 60 }}>Kỳ thứ</th>
                                        <th>Học phần</th>
                                        <th style={{ width: 60 }}>Số TC</th>
                                        <th style={{ width: 90 }}>Nhóm chung</th>
                                        <th style={{ width: 90 }}>Nhóm tự chọn</th>
                                        <th style={{ width: 200 }}>Ràng buộc</th>
                                        {hasTuition && (
                                            <>
                                                <th style={{ width: 90 }}>Số tiền</th>
                                                <th style={{ width: 90 }}>Miễn giảm</th>
                                                <th style={{ width: 90 }}>Phải nộp</th>
                                            </>
                                        )}
                                        <th style={{ width: 100 }}>#</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {classesLoading && filteredSubjects.length === 0 ? (
                                        renderLoadingRow(hasTuition ? 11 : 8)
                                    ) : filteredSubjects.length === 0 ? (
                                        <tr>
                                            <td colSpan={hasTuition ? 11 : 8} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                                Không có học phần nào.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredSubjects.map((s, idx) => {
                                            // KHÔNG validate canRegister ở Bảng 2 (môn) — vì 1 môn có nhiều lớp,
                                            // canRegister của lớp đầu tiên không đại diện cho cả nhóm.
                                            // Validate per-lớp ở Bảng 3 (cột # nút "Đăng ký" disabled khi !canRegister).
                                            const isActive = selectedSubjectKey === s.subjectKey;

                                            return (
                                                <tr
                                                    key={s.subjectKey}
                                                    className={isActive ? "old-row-active" : undefined}
                                                >
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td className="text-center">{s.ky_thu ?? "—"}</td>
                                                    <td>
                                                        <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                            {s.ky_hieu ? `${s.ky_hieu} - ` : ""}
                                                            {s.ten_mon}
                                                        </p>
                                                        {s.tinh_chat_lop_text && (
                                                            <span className="old-badge old-badge-primary">
                                                                {s.tinh_chat_lop_text}
                                                            </span>
                                                        )}
                                                        {/* {s.hinh_thuc_dang_ky_text && (
                                                            <span className="old-badge old-badge-success">
                                                                {s.hinh_thuc_dang_ky_text}
                                                            </span>
                                                        )} */}
                                                    </td>
                                                    <td className="text-center">{s.so_tin_chi}</td>
                                                    <td className="text-center">
                                                        {/* Phase 1: backend trả "" (ESS chưa expose nhom_TuChon_Chung). Phase 2 sẽ có data. */}
                                                        {s.nhom_TuChon_Chung ?? ""}
                                                    </td>
                                                    <td className="text-center">{s.nhom_tu_chon_text ?? ""}</td>
                                                    <td>{s.rang_buoc_text ?? ""}</td>
                                                    {hasTuition && (() => {
                                                        // Cùng môn = cùng tiền cho cùng SV (đơn giá theo id_lop SV,
                                                        // tinh_chat theo lịch sử SV với môn — class-independent). Lấy
                                                        // class đầu tiên có so_tien > 0 (fallback class[0]) để tránh
                                                        // 0đ nếu môn có 1 lớp đặc biệt xếp trước.
                                                        const sample = s.classes.find(c => (c.so_tien ?? 0) > 0) ?? s.classes[0];
                                                        const tien = sample?.so_tien ?? 0;
                                                        const giam = sample?.so_tien_mien_giam ?? 0;
                                                        const phai = sample?.so_tien_phai_nop ?? 0;
                                                        return (
                                                            <>
                                                                <td className="text-right">{tien.toLocaleString("vi-VN")}</td>
                                                                <td className="text-right">{giam.toLocaleString("vi-VN")}</td>
                                                                <td className="text-right">{phai.toLocaleString("vi-VN")}</td>
                                                            </>
                                                        );
                                                    })()}
                                                    <td className="text-center">
                                                        {isActive ? (
                                                            <button
                                                                className="old-btn old-btn-outline-danger"
                                                                onClick={() => handleSelectSubject(s.subjectKey)}
                                                            >
                                                                Bỏ chọn
                                                            </button>
                                                        ) : (
                                                            <button
                                                                className="old-btn old-btn-outline-primary"
                                                                onClick={() => handleSelectSubject(s.subjectKey)}
                                                            >
                                                                Chọn
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Box>

                    {/* ----- listDetail 50% ----- */}
                    <Box id="listDetail" sx={{ width: ["100%", "100%", "50%"] }}>
                        <div className="old-section-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <span>DANH SÁCH CÁC LỚP TÍN CHỈ ĐƯỢC LỰA CHỌN ĐĂNG KÝ</span>
                            {selectedSubject && (
                                <button
                                    className="old-btn old-btn-outline-primary"
                                    onClick={handleRefreshSlots}
                                    title="Tải lại số chỗ trống"
                                    style={{ fontSize: 12, padding: "4px 10px" }}
                                >
                                    <i className="fas fa-sync-alt" /> Tải lại
                                </button>
                            )}
                        </div>

                        <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "60vh" }}>
                            <table className="old-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                        <th rowSpan={2}>Tên lớp</th>
                                        <th colSpan={4}>Thời khóa biểu</th>
                                        <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                        <th rowSpan={2} style={{ width: 80 }}>Còn trống</th>
                                        {hasTuition && (
                                            <>
                                                <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                            </>
                                        )}
                                        <th rowSpan={2} style={{ width: 80 }}>#</th>
                                    </tr>
                                    <tr>
                                        <th>Thời gian</th>
                                        <th>Thứ</th>
                                        <th>Tiết</th>
                                        <th>Phòng</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {!selectedSubject ? (
                                        <tr>
                                            <td colSpan={hasTuition ? 12 : 9} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                                Chọn học phần đăng ký học
                                            </td>
                                        </tr>
                                    ) : (
                                        selectedSubject.classes.flatMap((c, idx) => {
                                            const isFull = c.slotsLeft <= 0;
                                            const isLocked = isFull || !c.canRegister || !!registerGateReason;
                                            const isPending = pendingClassIds.includes(c.classId);
                                            const isConfirmed = confirmedClassIds.includes(c.classId);
                                            const isAlreadyRegistered = myList.some(
                                                r => r.id_lop_tc === c.classId && r.trang_thai === 1
                                            );
                                            const hasLth = !!(c.listThucHanh && c.listThucHanh.length > 0);
                                            const hasSh = !!(c.listSongHanh && c.listSongHanh.length > 0);
                                            const isExpanded = expandedSub?.classId === c.classId;

                                            const cellsBefore = [
                                                idx + 1,
                                                <Box>
                                                    <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                        {c.ten_lop_hp}
                                                    </p>
                                                    {c.hinh_thuc_dang_ky_text && (
                                                        <span className="old-badge old-badge-primary">
                                                            {c.hinh_thuc_dang_ky_text}
                                                        </span>
                                                    )}
                                                </Box>,
                                            ];

                                            // Rule 12: SV đã đăng ký lớp khác cùng môn → ẩn nút Đăng ký, show text.
                                            const isSameMonRegistered = c.reasonCode === "ALREADY_REGISTERED_SAME_MON";

                                            // Multi-state button cột # (rule Old project):
                                            //   Đã ĐK / Pending → giữ nguyên trạng thái
                                            //   isSameMonRegistered → text "Đã ĐK môn này" (rule 12)
                                            //   hasLth → "Chọn LTH" toggle expand sub-table thực hành
                                            //   hasSh  → "Chọn SH"  toggle expand sub-table song hành
                                            //   else   → "Đăng ký" (đăng ký đơn lẻ)
                                            const finBlock = getFinancialBlock(c, financial);
                                            const actionButton = isAlreadyRegistered || isConfirmed ? (
                                                <span className="text-success">
                                                    <i className="fas fa-check" /> Đã ĐK
                                                </span>
                                            ) : isPending ? (
                                                <button className="old-btn old-btn-outline-primary" disabled>
                                                    <Spinner size="small" />
                                                </button>
                                            ) : isSameMonRegistered ? (
                                                <span style={{ color: "#888", fontStyle: "italic", fontSize: 12 }}>
                                                    Đã ĐK môn này
                                                </span>
                                            ) : finBlock ? (
                                                // FE gate tài chính (strict mode mirror backend EligibilityService):
                                                // ẩn hoàn toàn nút Đăng ký + Chọn LTH/SH khi SV không có hồ sơ TC
                                                // hoặc không đủ số dư. Banner ở widget đã giải thích lý do tổng quát.
                                                <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                    {finBlock}
                                                </span>
                                            ) : hasLth ? (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className={`old-btn ${isExpanded ? "old-btn-outline-danger" : "old-btn-outline-primary"}`}
                                                        disabled={isLocked}
                                                        onClick={() => setExpandedSub(prev =>
                                                            prev?.classId === c.classId ? null : { type: "lth", classId: c.classId }
                                                        )}
                                                    >
                                                        {isExpanded ? "Đóng" : "Chọn LTH"}
                                                    </button>,
                                                    registerGateReason
                                                )
                                            ) : hasSh ? (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className={`old-btn ${isExpanded ? "old-btn-outline-danger" : "old-btn-outline-primary"}`}
                                                        disabled={isLocked}
                                                        onClick={() => setExpandedSub(prev =>
                                                            prev?.classId === c.classId ? null : { type: "sh", classId: c.classId }
                                                        )}
                                                    >
                                                        {isExpanded ? "Đóng" : "Chọn SH"}
                                                    </button>,
                                                    registerGateReason
                                                )
                                            ) : (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className="old-btn old-btn-outline-primary"
                                                        disabled={isLocked}
                                                        onClick={() => handleRegister(c.classId)}
                                                    >
                                                        Đăng ký
                                                    </button>,
                                                    registerGateReason
                                                )
                                            );

                                            const cellsAfter = [
                                                isFull ? (
                                                    <span className="text-danger">Hết</span>
                                                ) : (
                                                    <strong>{c.slotsLeft}</strong>
                                                ),
                                                ...(hasTuition ? [
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                ] : []),
                                                actionButton,
                                            ];

                                            const hasConflict = !!(c.iD_lops_TrungLich && c.iD_lops_TrungLich.length > 0);
                                            const highlightClass = isAlreadyRegistered
                                                ? undefined
                                                : (isFull || hasConflict ? "old-row-locked" : undefined);

                                            const tkbRows = renderTKBRows(
                                                c.thoiKhoaBieus,
                                                cellsBefore,
                                                cellsAfter,
                                                `cls-${c.classId}`,
                                                highlightClass,
                                                c.giao_vien
                                            );

                                            // Phase 2A: render sub-table khi expanded.
                                            // onPickPair = handleRegister single (Phase 2C sẽ thay thành register pair atomic).
                                            const subTable = isExpanded
                                                ? renderSubTable(
                                                    expandedSub!.type === "lth" ? c.listThucHanh! : c.listSongHanh!,
                                                    expandedSub!.type,
                                                    c.classId,
                                                    hasTuition ? 12 : 9,
                                                    myList,
                                                    pendingClassIds,
                                                    (parentId, childId) =>
                                                        dispatch(rootActions.registration.REGISTER_PAIR_START({
                                                            parentClassId: parentId,
                                                            childClassId: childId,
                                                        })),
                                                    hasTuition,
                                                    financial,
                                                    registerGateReason
                                                )
                                                : null;

                                            return [tkbRows, subTable].filter(Boolean);
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {selectedSubject && (
                            <Text
                                as="p"
                                sx={{
                                    textAlign: "right",
                                    fontStyle: "italic",
                                    mt: "3px",
                                    fontSize: 1,
                                    color: "#666",
                                }}
                            >
                                Lưu ý: Màu vàng là trùng thời khóa biểu hoặc hết chỗ đăng ký - sẽ không đăng ký được
                            </Text>
                        )}
                    </Box>
                    </Box>
                    ) : (
                    /* ===================== B4: listDetailWithSubject (15 cột combined) ===================== */
                    <Box id="listDetailWithSubject" sx={{ width: "100%" }}>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
                            <button
                                className="old-btn old-btn-outline-primary"
                                onClick={handleRefreshSlots}
                                title="Tải lại số chỗ trống"
                                style={{ fontSize: 12, padding: "4px 10px" }}
                            >
                                <i className="fas fa-sync-alt" /> Tải lại
                            </button>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                            <table className="old-table">
                                <thead>
                                    <tr>
                                        <th rowSpan={2} style={{ width: 50 }}>STT</th>
                                        <th rowSpan={2} style={{ width: 60 }}>Kỳ thứ</th>
                                        <th rowSpan={2}>Học phần</th>
                                        <th rowSpan={2} style={{ width: 60 }}>Số TC</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Nhóm chung</th>
                                        <th rowSpan={2} style={{ width: 90 }}>Nhóm tự chọn</th>
                                        <th rowSpan={2} style={{ width: 200 }}>Ràng buộc</th>
                                        <th rowSpan={2} style={{ width: 130 }}>Tên lớp</th>
                                        <th colSpan={4}>Thời khóa biểu</th>
                                        <th rowSpan={2} style={{ width: 130 }}>Giáo viên</th>
                                        <th rowSpan={2} style={{ width: 80 }}>Còn trống</th>
                                        {hasTuition && (
                                            <>
                                                <th rowSpan={2} style={{ width: 90 }}>Số tiền</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Miễn giảm</th>
                                                <th rowSpan={2} style={{ width: 90 }}>Phải nộp</th>
                                            </>
                                        )}
                                        <th rowSpan={2} style={{ width: 80 }}>#</th>
                                    </tr>
                                    <tr>
                                        <th>Thời gian</th>
                                        <th>Thứ</th>
                                        <th>Tiết</th>
                                        <th>Phòng</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {classesLoading && filteredCombinedClasses.length === 0 ? (
                                        renderLoadingRow(hasTuition ? 18 : 15)
                                    ) : filteredCombinedClasses.length === 0 ? (
                                        <tr>
                                            <td colSpan={hasTuition ? 18 : 15} style={{ textAlign: "center", padding: 20, color: "#888" }}>
                                                Không có lớp nào trong nhóm này.
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredCombinedClasses.flatMap((c, idx) => {
                                            const isFull = c.slotsLeft <= 0;
                                            const isLocked = isFull || !c.canRegister || !!registerGateReason;
                                            const isPending = pendingClassIds.includes(c.classId);
                                            const isConfirmed = confirmedClassIds.includes(c.classId);
                                            const isAlreadyRegistered = myList.some(
                                                r => r.id_lop_tc === c.classId && r.trang_thai === 1
                                            );
                                            const hasLth = !!(c.listThucHanh && c.listThucHanh.length > 0);
                                            const hasSh = !!(c.listSongHanh && c.listSongHanh.length > 0);
                                            const isExpanded = expandedSub?.classId === c.classId;

                                            const cellsBefore = [
                                                idx + 1,
                                                c.ky_thu ?? "—",
                                                <Box>
                                                    <p style={{ fontWeight: 600, marginBottom: 4 }}>
                                                        {c.ky_hieu ? `${c.ky_hieu} - ` : ""}
                                                        {c.ten_mon}
                                                    </p>
                                                    {c.tinh_chat_lop_text && (
                                                        <span className="old-badge old-badge-primary">
                                                            {c.tinh_chat_lop_text}
                                                        </span>
                                                    )}
                                                    {c.hinh_thuc_dang_ky_text && (
                                                        <span className="old-badge old-badge-success">
                                                            {c.hinh_thuc_dang_ky_text}
                                                        </span>
                                                    )}
                                                </Box>,
                                                c.so_tin_chi,
                                                c.nhom_TuChon_Chung ?? "",
                                                c.nhom_tu_chon_text ?? "",
                                                c.rang_buoc_text ?? "",
                                                c.ten_lop_hp,
                                            ];

                                            // Rule 12: SV đã đăng ký lớp khác cùng môn → ẩn nút Đăng ký, show text.
                                            const isSameMonRegistered = c.reasonCode === "ALREADY_REGISTERED_SAME_MON";

                                            const finBlock = getFinancialBlock(c, financial);
                                            const actionButton = isAlreadyRegistered || isConfirmed ? (
                                                <span className="text-success">
                                                    <i className="fas fa-check" /> Đã ĐK
                                                </span>
                                            ) : isPending ? (
                                                <button className="old-btn old-btn-outline-primary" disabled>
                                                    <Spinner size="small" />
                                                </button>
                                            ) : isSameMonRegistered ? (
                                                <span style={{ color: "#888", fontStyle: "italic", fontSize: 12 }}>
                                                    Đã ĐK môn này
                                                </span>
                                            ) : finBlock ? (
                                                // FE gate tài chính (strict mode mirror backend EligibilityService):
                                                // ẩn hoàn toàn nút Đăng ký + Chọn LTH/SH khi SV không có hồ sơ TC
                                                // hoặc không đủ số dư. Banner ở widget đã giải thích lý do tổng quát.
                                                <span style={{ color: "#a30f26", fontStyle: "italic", fontSize: 12 }}>
                                                    {finBlock}
                                                </span>
                                            ) : hasLth ? (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className={`old-btn ${isExpanded ? "old-btn-outline-danger" : "old-btn-outline-primary"}`}
                                                        disabled={isLocked}
                                                        onClick={() => setExpandedSub(prev =>
                                                            prev?.classId === c.classId ? null : { type: "lth", classId: c.classId }
                                                        )}
                                                    >
                                                        {isExpanded ? "Đóng" : "Chọn LTH"}
                                                    </button>,
                                                    registerGateReason
                                                )
                                            ) : hasSh ? (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className={`old-btn ${isExpanded ? "old-btn-outline-danger" : "old-btn-outline-primary"}`}
                                                        disabled={isLocked}
                                                        onClick={() => setExpandedSub(prev =>
                                                            prev?.classId === c.classId ? null : { type: "sh", classId: c.classId }
                                                        )}
                                                    >
                                                        {isExpanded ? "Đóng" : "Chọn SH"}
                                                    </button>,
                                                    registerGateReason
                                                )
                                            ) : (
                                                withLockedRegisterTooltip(
                                                    isLocked,
                                                    c,
                                                    isFull,
                                                    <button
                                                        className="old-btn old-btn-outline-primary"
                                                        disabled={isLocked}
                                                        onClick={() => handleRegister(c.classId)}
                                                    >
                                                        Đăng ký
                                                    </button>,
                                                    registerGateReason
                                                )
                                            );

                                            const cellsAfter = [
                                                isFull ? (
                                                    <span className="text-danger">Hết</span>
                                                ) : (
                                                    <strong>{c.slotsLeft}</strong>
                                                ),
                                                ...(hasTuition ? [
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_mien_giam ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                    <span style={{ display: "block", textAlign: "right" }}>
                                                        {(c.so_tien_phai_nop ?? 0).toLocaleString("vi-VN")}
                                                    </span>,
                                                ] : []),
                                                actionButton,
                                            ];

                                            const hasConflict = !!(c.iD_lops_TrungLich && c.iD_lops_TrungLich.length > 0);
                                            const highlightClass = isAlreadyRegistered
                                                ? undefined
                                                : (isFull || hasConflict ? "old-row-locked" : undefined);

                                            const tkbRows = renderTKBRows(
                                                c.thoiKhoaBieus,
                                                cellsBefore,
                                                cellsAfter,
                                                `combined-${c.classId}`,
                                                highlightClass,
                                                c.giao_vien
                                            );

                                            // Bảng 4 colSpan = 15 (18 khi có cột tiền).
                                            const subTable = isExpanded
                                                ? renderSubTable(
                                                    expandedSub!.type === "lth" ? c.listThucHanh! : c.listSongHanh!,
                                                    expandedSub!.type,
                                                    c.classId,
                                                    hasTuition ? 18 : 15,
                                                    myList,
                                                    pendingClassIds,
                                                    (parentId, childId) =>
                                                        dispatch(rootActions.registration.REGISTER_PAIR_START({
                                                            parentClassId: parentId,
                                                            childClassId: childId,
                                                        })),
                                                    hasTuition,
                                                    financial,
                                                    registerGateReason
                                                )
                                                : null;

                                            return [tkbRows, subTable].filter(Boolean);
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>

                        <Text
                            as="p"
                            sx={{
                                textAlign: "right",
                                fontStyle: "italic",
                                mt: "3px",
                                fontSize: 1,
                                color: "#666",
                            }}
                        >
                            Lưu ý: Màu vàng là trùng thời khóa biểu hoặc hết chỗ đăng ký - sẽ không đăng ký được
                        </Text>
                    </Box>
                    )}
                    </>
                    )}
                </Box>
            )}

            {/* Confirm dialogs */}
            {confirmCancel && (
                <ConfirmationDialog
                    title="Xác nhận hủy đăng ký"
                    onClose={(g) => (g === "confirm" ? handleCancelConfirm() : setConfirmCancel(null))}
                    confirmButtonContent="Xác nhận hủy"
                    confirmButtonType="danger"
                    cancelButtonContent="Đóng"
                >
                    Bạn có chắc muốn hủy đăng ký lớp{" "}
                    <strong>{confirmCancel.ten_lop_hp ?? `#${confirmCancel.id_lop_tc}`}</strong>?
                </ConfirmationDialog>
            )}

            {confirmChot && (
                <ConfirmationDialog
                    title="Xác nhận lưu dữ liệu đăng ký"
                    onClose={(g) => (g === "confirm" ? handleChotDuLieuConfirm() : setConfirmChot(false))}
                    confirmButtonContent="Lưu"
                    confirmButtonType="danger"
                    cancelButtonContent="Đóng"
                >
                    Lưu ý: Sinh viên tự chịu trách nhiệm với kết quả đăng ký tín chỉ. Bạn đã chắc chắn muốn lưu lại kết quả đăng ký không? Sau khi bấm lưu, sinh viên không được phép thay đổi kết quả đăng ký.
                </ConfirmationDialog>
            )}
        </PageLayout>
    );
};

export default ClassesPage;
